import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { 
  RefreshCw, 
  Download, 
  CheckCircle2, 
  AlertCircle,
  Cloud,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OniiSyncButton = ({ onImportComplete, variant = "outline", size = "default" }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [polling, setPolling] = useState(false);
  const [importing, setImporting] = useState(false);

  // Poll for status updates
  useEffect(() => {
    let interval;
    if (polling) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/onii/sync-status`);
          setSyncStatus(res.data);
          
          if (res.data.status === "completed" || res.data.status === "error") {
            setPolling(false);
          }
        } catch (error) {
          console.error("Error polling status:", error);
        }
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [polling]);

  const handleStartSync = async () => {
    setShowDialog(true);
    setSyncStatus({ status: "running", message: "Iniciando...", progress: 0 });
    setPolling(true);
    
    try {
      await axios.post(`${API}/onii/fetch-transactions`);
    } catch (error) {
      toast.error("Erro ao iniciar sincronização", {
        description: error.response?.data?.detail || "Tente novamente"
      });
      setPolling(false);
      setSyncStatus({ status: "error", message: "Erro ao iniciar" });
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/onii/import-preview`);
      toast.success("Transações importadas!", {
        description: `${res.data.sales_added} vendas adicionadas. ${res.data.stock_updated} produtos atualizados.`
      });
      setShowDialog(false);
      setSyncStatus(null);
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      toast.error("Erro ao importar", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/onii/cancel-sync`);
    } catch (error) {
      console.error("Error canceling:", error);
    }
    setShowDialog(false);
    setSyncStatus(null);
    setPolling(false);
  };

  const formatPreviewValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(value);
  };

  return (
    <>
      <Button
        onClick={handleStartSync}
        variant={variant}
        size={size}
        data-testid="onii-sync-btn"
        className="gap-2"
      >
        <Cloud className="w-4 h-4" />
        Buscar Transações Onii
      </Button>

      <Dialog open={showDialog} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Sincronização Onii
            </DialogTitle>
            <DialogDescription>
              Buscando transações do merchant.onii.app
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            {/* Status Display */}
            {syncStatus?.status === "running" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-sm">{syncStatus.message}</span>
                </div>
                <Progress value={syncStatus.progress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">
                  {syncStatus.progress}% concluído
                </p>
                {syncStatus.last_sale_date && (
                  <p className="text-xs text-muted-foreground">
                    Buscando vendas a partir de: <strong>{syncStatus.last_sale_date}</strong>
                  </p>
                )}
              </div>
            )}

            {syncStatus?.status === "error" && (
              <div className="flex flex-col items-center gap-3 py-6">
                <AlertCircle className="w-12 h-12 text-destructive" />
                <p className="text-sm text-destructive font-medium">{syncStatus.message}</p>
                <Button variant="outline" onClick={handleCancel}>
                  Fechar
                </Button>
              </div>
            )}

            {syncStatus?.status === "completed" && syncStatus.preview_data && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-600">{syncStatus.message}</span>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Período:</span>
                    <span className="font-medium">{syncStatus.preview_data.date_range}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Transações encontradas:</span>
                    <Badge variant={syncStatus.preview_data.new_transactions > 0 ? "default" : "secondary"}>
                      {syncStatus.preview_data.new_transactions}
                    </Badge>
                  </div>
                </div>

                {syncStatus.preview_data.new_transactions === 0 ? (
                  <div className="text-center py-4">
                    <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Todas as vendas já estão atualizadas!
                    </p>
                  </div>
                ) : syncStatus.preview_data.preview && syncStatus.preview_data.preview.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium mb-2">Prévia (primeiras 10 linhas):</p>
                    <div className="overflow-x-auto max-h-[300px] border rounded">
                      <table className="data-table text-xs">
                        <thead className="sticky top-0 bg-white">
                          <tr>
                            {syncStatus.preview_data.columns?.slice(0, 6).map((col, idx) => (
                              <th key={idx} className="whitespace-nowrap">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {syncStatus.preview_data.preview.map((row, idx) => (
                            <tr key={idx}>
                              {syncStatus.preview_data.columns?.slice(0, 6).map((col, colIdx) => (
                                <td key={colIdx} className="whitespace-nowrap max-w-[150px] truncate">
                                  {formatPreviewValue(row[col])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            {syncStatus?.status === "completed" && 
             syncStatus.preview_data?.new_transactions > 0 && (
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {importing ? "Importando..." : "Importar Transações"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OniiSyncButton;
